import React, { useState } from "react";

const LEVELS = [
  { elo: 0, en: "Beginner", pl: "Początek", es: "Principiante" },
  { elo: 800, en: "Starter", pl: "Starter", es: "Inicio" },
  { elo: 1200, en: "Squire", pl: "Giermek", es: "Escudero" },
  { elo: 1600, en: "Guardian", pl: "Strażnik", es: "Guardián" },
  { elo: 2000, en: "Castle", pl: "Zamek", es: "Castillo" },
  { elo: 2400, en: "Master", pl: "Mistrz", es: "Maestro" },
  { elo: 3000, en: "Super GM", pl: "Super GM", es: "Super GM" },
];

function label(lang, value) {
  return value?.[lang] || value?.en || "";
}

export default function HoodLevelPicker({
  lang = "en",
  currentElo = 0,
  onSelect,
  compact = false,
  allowBeginner = true,
  ownerQa = false,
  accountAgeDays = 30,
  onAccountAgeChange,
}) {
  const [open, setOpen] = useState(false);
  const visibleLevels = allowBeginner ? LEVELS : LEVELS.filter((row) => row.elo > 0);
  const current = visibleLevels.reduce((best, row) =>
    Math.abs(row.elo - Number(currentElo || 0)) < Math.abs(best.elo - Number(currentElo || 0)) ? row : best
  , visibleLevels[0] || LEVELS[0]);

  const copy = ownerQa
    ? (lang === "pl"
        ? { button: "Ranking QA", title: "Zmień ranking testowy", note: "Tylko dla właściciela: symuluje ranking do testów. Nie zmienia prawdziwego rankingu konta." }
        : lang === "es"
          ? { button: "Rating QA", title: "Cambiar rating de prueba", note: "Solo para el propietario: simula el rating para pruebas. No cambia el rating real de la cuenta." }
          : { button: "QA rating", title: "Change test rating", note: "Owner-only: simulates rating for testing. It does not change your real account rating." })
    : (lang === "pl"
        ? { button: "Zmień poziom", title: "Wybierz poziom treningu", note: "To zmienia trudność Hooda i Stockfisha, nie Twój prawdziwy ranking." }
        : lang === "es"
          ? { button: "Cambiar nivel", title: "Elige el nivel de entrenamiento", note: "Esto cambia la dificultad de Hood y Stockfish, no tu rating real." }
          : { button: "Change level", title: "Choose training level", note: "This changes Hood and Stockfish difficulty, not your real rating." });

  return <>
    <button
      type="button"
      onClick={() => setOpen(true)}
      style={{
        border: "1px solid rgba(125,211,252,.55)",
        background: "rgba(14,116,144,.14)",
        color: "#d9f3ff",
        borderRadius: 999,
        minHeight: compact ? 34 : 40,
        padding: compact ? "6px 10px" : "8px 13px",
        fontWeight: 850,
        fontSize: compact ? 12 : 13,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {ownerQa
        ? `QA ${Math.round(Number(currentElo)||0)} · Day ${Math.round(Number(accountAgeDays)||0)}`
        : `${copy.button} · ${current.elo === 0 ? label(lang, current) : current.elo}`}
    </button>

    {open && <div
      role="presentation"
      onClick={() => setOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(2,8,23,.76)",
        display: "grid",
        placeItems: "center",
        padding: 18,
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={copy.title}
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(94vw,430px)",
          background: "#102b44",
          border: "1px solid rgba(125,211,252,.35)",
          borderRadius: 22,
          padding: 18,
          boxShadow: "0 20px 60px rgba(0,0,0,.45)",
          color: "#f8fafc",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 21 }}>{copy.title}</h2>
          <button type="button" onClick={() => setOpen(false)} style={{ border: 0, background: "transparent", color: "#fff", fontSize: 26 }}>×</button>
        </div>
        <p style={{ color: "#afc2d8", fontSize: 13, lineHeight: 1.45, margin: "8px 0 15px" }}>{copy.note}</p>
        {ownerQa && <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, fontWeight: 900 }}>
            <span>{lang === "pl" ? "Ranking testowy" : lang === "es" ? "Rating de prueba" : "Test rating"}</span>
            <span style={{ color: "#FFD400" }}>{Math.round(Number(currentElo)||0)}</span>
          </div>
          <input
            type="range"
            min="0"
            max="3000"
            step="25"
            value={Math.round(Number(currentElo)||0)}
            onChange={(event) => onSelect?.(Number(event.target.value))}
            style={{ width: "100%", marginTop: 8 }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", color: "#afc2d8", fontSize: 10 }}>
            <span>0</span><span>800</span><span>1200</span><span>1600</span><span>2000</span><span>2400</span><span>3000</span>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, fontWeight: 900, marginTop: 18 }}>
            <span>{lang === "pl" ? "Wiek użytkownika" : lang === "es" ? "Edad de la cuenta" : "User age"}</span>
            <span style={{ color: "#7dd3fc" }}>Day {Math.round(Number(accountAgeDays)||0)}</span>
          </div>
          <input
            type="range"
            min="0"
            max="365"
            step="1"
            value={Math.round(Number(accountAgeDays)||0)}
            onChange={(event) => onAccountAgeChange?.(Number(event.target.value))}
            style={{ width: "100%", marginTop: 8 }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", color: "#afc2d8", fontSize: 10 }}>
            <span>First visit</span><span>7d</span><span>30d</span><span>90d</span><span>1y</span>
          </div>
        </div>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
          {visibleLevels.map((row) => {
            const selected = row.elo === current.elo;
            return <button
              key={row.elo}
              type="button"
              onClick={() => {
                onSelect?.(row.elo);
                setOpen(false);
              }}
              style={{
                border: selected ? "2px solid #FFD400" : "1px solid rgba(255,255,255,.14)",
                background: selected ? "rgba(255,212,0,.10)" : "#17344c",
                color: "#fff",
                borderRadius: 14,
                minHeight: 58,
                padding: "9px 10px",
                textAlign: "left",
                cursor: "pointer",
              }}
            >
              <div style={{ fontWeight: 900 }}>{label(lang, row)}</div>
              <div style={{ color: selected ? "#FFD400" : "#afc2d8", fontSize: 12, marginTop: 2 }}>
                {row.elo === 0 ? "0 · basics" : `ELO ~${row.elo}`}
              </div>
            </button>;
          })}
        </div>
      </section>
    </div>}
  </>;
}

export { LEVELS as HOOD_TRAINING_LEVELS };
